import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Container,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { LANDING_ASSETS, NAV_LINKS } from '../../content/landingContent';
import { homeSectionTo, isHomeSectionHash } from '../../utils/landingLinks';

const NAV_FONT_SIZE = 16;
/** Inner landing routes without hero — navbar always uses scrolled (light) style */
const INNER_LANDING_PATHS = ['/calculator', '/methods-guide', '/calculation-guide'];

const LandingNavbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isInnerPage = INNER_LANDING_PATHS.some((p) => location.pathname.startsWith(p));
  const navScrolled = isInnerPage || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const logoSet = isMobile ? LANDING_ASSETS.logo.mobile : LANDING_ASSETS.logo.desktop;
  const logoSrc = navScrolled ? logoSet.scrolled : logoSet.hero;

  const navLinkSx = {
    fontFamily: 'Cabin, sans-serif',
    fontSize: NAV_FONT_SIZE,
    fontWeight: 400,
    color: navScrolled ? '#000' : '#fff',
    transition: 'color 0.25s ease',
    '&:hover': { color: navScrolled ? '#333' : 'rgba(255,255,255,0.85)' },
  };

  const renderLink = (item) => {
    if (item.internal) {
      return (
        <Link key={item.label} component={RouterLink} to={item.href} sx={navLinkSx}>
          {item.label}
        </Link>
      );
    }

    if (item.external) {
      return (
        <Link
          key={item.label}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          sx={navLinkSx}
        >
          {item.label}
        </Link>
      );
    }

    // #process, #stories — must go to /#section, not /calculator#section
    if (isHomeSectionHash(item.href)) {
      return (
        <Link key={item.label} component={RouterLink} to={homeSectionTo(item.href)} sx={navLinkSx}>
          {item.label}
        </Link>
      );
    }

    return (
      <Link key={item.label} href={item.href} sx={navLinkSx}>
        {item.label}
      </Link>
    );
  };

  const renderDrawerItem = (item) => {
    const textProps = {
      primary: item.label,
      primaryTypographyProps: { fontFamily: 'Cabin, sans-serif', fontSize: NAV_FONT_SIZE },
    };

    if (item.internal) {
      return (
        <ListItem key={item.label} disablePadding>
          <ListItemButton component={RouterLink} to={item.href} onClick={() => setOpen(false)}>
            <ListItemText {...textProps} />
          </ListItemButton>
        </ListItem>
      );
    }

    if (isHomeSectionHash(item.href)) {
      return (
        <ListItem key={item.label} disablePadding>
          <ListItemButton
            component={RouterLink}
            to={homeSectionTo(item.href)}
            onClick={() => setOpen(false)}
          >
            <ListItemText {...textProps} />
          </ListItemButton>
        </ListItem>
      );
    }

    return (
      <ListItem key={item.label} disablePadding>
        <ListItemButton
          component="a"
          href={item.href}
          target={item.external ? '_blank' : undefined}
          rel={item.external ? 'noopener noreferrer' : undefined}
          onClick={() => setOpen(false)}
        >
          <ListItemText {...textProps} />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: navScrolled ? '#fff' : 'transparent',
          borderBottom: navScrolled ? '1px solid #e2e2e2' : 'none',
          boxShadow: navScrolled ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
          transition: 'background-color 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: 64, md: 80 } }}>
            <RouterLink to="/">
              <Box
                component="img"
                src={logoSrc}
                alt="Hype"
                sx={{
                  height: navScrolled ? { xs: 24, md: 32 } : { xs: 28, md: 36 },
                  width: 'auto',
                  display: 'block',
                  transition: 'height 0.2s ease',
                }}
              />
            </RouterLink>

            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {NAV_LINKS.map(renderLink)}
              </Box>
            )}

            {isMobile && (
              <IconButton
                onClick={() => setOpen(true)}
                aria-label="Menü"
                sx={{ color: navScrolled ? '#000' : '#fff' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 280, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List>{NAV_LINKS.map(renderDrawerItem)}</List>
        </Box>
      </Drawer>
    </>
  );
};

export default LandingNavbar;
